/**
 * Bridge Session Source
 *
 * The web app's session-event transport is the shared per-tab cloud bridge
 * (`BridgeEventStream`) — NOT REST polling. Two collaborators wire it up:
 *
 *   - `SessionRegistrar` — a thin object that turns "register/deregister
 *     interest in a session" into the server-managed HTTP control-plane actions
 *     (`POST .../subscribe` / `.../unsubscribe`). It NEVER sends a
 *     JSON-RPC subscribe frame over the bridge (bridge ADR-003); the subscription row
 *     is written server-side as a side effect of the API call. Both methods
 *     REJECT loudly on a non-2xx — no silent swallow (`.claude/rules/no-fallbacks`).
 *
 *   - `BridgeSessionSource` — the facade the chat layer consumes. It
 *     wraps a `BridgeEventStream` + a `SessionRegistrar` (the SAME registrar the
 *     stream re-issues `subscribe` through on reconnect) and exposes
 *     `registerSession`, delegating to the stream's demux registry.
 */

import type { HttpLayer } from '../http.js';
import type { BridgeEventStream, LlmProgressHandler, SessionEventHandler } from './bridge-events.js';

/**
 * Turns session-interest register/deregister into the HTTP control-plane
 * actions. The stream depends only on this narrow surface; the concrete
 * {@link HttpSessionRegistrar} implements it over the SDK http client.
 */
export interface SessionRegistrar {
  /**
   * Register the connection `deviceId` as a fanout target for `sessionId` and
   * trigger a replay of persisted events after `afterSequence`. Rejects on a
   * non-2xx.
   */
  subscribe(sessionId: string, deviceId: string, afterSequence: number): Promise<void>;
  /** Delete the `(deviceId, "session", sessionId)` subscription. Rejects on a non-2xx. */
  unsubscribe(sessionId: string, deviceId: string): Promise<void>;
}

/**
 * `SessionRegistrar` backed by the SDK http client hitting the subscribe/unsubscribe
 * endpoints. Selects the workspace-scoped vs workspace-less URL exactly the way
 * `ChatSession.sessionsPrefix()` does — `workspaceId ? '/v1/sessions/{ws}' :
 * '/v1/sessions'`. The per-session workspace is recorded via
 * {@link noteSessionWorkspace} (web chats are workspace-less, so the default is
 * the workspace-less sibling route); it is held on the registrar — NOT threaded
 * per call — so a reconnect re-`subscribe` (stream `onopen`) resolves the same
 * URL without the stream needing to carry the workspace id.
 */
export class HttpSessionRegistrar implements SessionRegistrar {
  private readonly sessionWorkspaces = new Map<string, string | undefined>();

  constructor(private readonly http: HttpLayer) {}

  /**
   * Record the workspace lane for `sessionId` so subsequent subscribe/unsubscribe
   * calls (including reconnect re-registration) select the right URL. Absent /
   * `undefined` → the workspace-less sibling route.
   */
  noteSessionWorkspace(sessionId: string, workspaceId?: string | null): void {
    this.sessionWorkspaces.set(sessionId, workspaceId ?? undefined);
  }

  /** Mirror of `ChatSession.sessionsPrefix()`. */
  private prefix(sessionId: string): string {
    const workspaceId = this.sessionWorkspaces.get(sessionId);
    return workspaceId ? `/v1/sessions/${workspaceId}` : `/v1/sessions`;
  }

  async subscribe(sessionId: string, deviceId: string, afterSequence: number): Promise<void> {
    await this.http.post<void>(`${this.prefix(sessionId)}/sessions/${sessionId}/subscribe`, {
      device_id: deviceId,
      after_sequence: afterSequence,
    });
  }

  async unsubscribe(sessionId: string, deviceId: string): Promise<void> {
    await this.http.post<void>(`${this.prefix(sessionId)}/sessions/${sessionId}/unsubscribe`, {
      device_id: deviceId,
    });
  }
}

/**
 * The chat-layer-facing facade. Owns the shared per-tab
 * `BridgeEventStream` and its `HttpSessionRegistrar`; each `ChatSession` calls
 * {@link registerSession} to attach its handler to the demux and open its
 * subscription.
 */
export class BridgeSessionSource {
  constructor(
    private readonly stream: BridgeEventStream,
    private readonly registrar: HttpSessionRegistrar,
  ) {}

  /**
   * Attach `handler` to the shared stream's per-session demux and open the
   * subscription. `cursor` is a live provider of the caller's highest-seen
   * `sequence_num` (read on register AND on every reconnect re-registration).
   * `workspaceId` selects the workspace-scoped route; omit for the workspace-less
   * web lane. Returns the stream's `{ registered, unsubscribe }` — `registered`
   * is the initial-subscribe promise the caller MUST await/surface.
   */
  registerSession(
    sessionId: string,
    handler: SessionEventHandler,
    cursor: () => number,
    workspaceId?: string | null,
  ): { registered: Promise<void>; unsubscribe: () => Promise<void> } {
    this.registrar.noteSessionWorkspace(sessionId, workspaceId);
    return this.stream.registerSession(sessionId, handler, cursor);
  }

  /**
   * Same-socket re-subscribe for a session on a detected sub-max gap.
   * Delegates to the stream's imperative
   * {@link BridgeEventStream.reregister}, which re-issues `subscribe` with the
   * session's current (contiguousMax) cursor over the live socket. Rejects loudly.
   */
  reregister(sessionId: string): Promise<void> {
    return this.stream.reregister(sessionId);
  }

  /** Receive a registered session's `agent.llmProgress` frames (agents ADR-01674). */
  onLlmProgress(sessionId: string, handler: LlmProgressHandler): () => void {
    return this.stream.onLlmProgress(sessionId, handler);
  }
}
